// ============================================================
// How a road is named on a card
// ============================================================
// Its own module so it can be tested without a browser or a React tree —
// deriving a shield from an address is not drawing. Same reasoning, and the
// same output, as data/RoadLine.kt in the Android app; the two apps have to
// name a road identically or a driver meets two different products.


// Road-shield helper — derives a Dutch road shield (A-roads red, N-roads yellow,
// city roads neutral) from a report's address/city. Purely presentational.
export const getRoadShield = (address, city) => {
  const text = `${address || ""} ${city || ""}`;
  const hw = text.match(/\b([AaNn])\s?(\d{1,3})\b/);
  if (hw) {
    const kind = hw[1].toUpperCase() === "A" ? "a" : "n";
    return { kind, label: `${hw[1].toUpperCase()}${hw[2]}` };
  }
  return { kind: "s", label: "STAD" };
};

/**
 * A board row's top line: the road, with its shield when it has a number.
 *
 * The street used to be the smallest, greyest, truncated line on the card —
 * "Langbroekdreef, Zuidoost, Amst…" — while the widest line said "Mobiele
 * flitser" beside a badge that said "FLIT". Three rows in a row looked
 * identical even though the only thing that mattered, where it is, differed on
 * every one of them. So the road moves up and the service moves down.
 *
 * Returns { shield, road, rest }:
 *  - shield: an A/N shield, or null in town. No neutral "STAD" box — that
 *    spends the widest slot on a word every urban row repeats. Its absence
 *    already says "not a motorway".
 *  - road:   the street, with the road number lifted out of it so "A10
 *            Coenplein" reads as a shield followed by "Coenplein".
 *  - rest:   district and city, for the line below.
 */
export const roadLine = (m) => {
  const parts = String(m.address || "").split(",").map(s => s.trim()).filter(Boolean);
  let road = parts[0] || m.city || "";
  // Whatever the address had after the street; the city on its own when it had
  // nothing, and nothing at all when the city is already the road we printed.
  const rest = parts.length > 1
    ? parts.slice(1).join(", ")
    : (m.city && m.city !== road ? m.city : "");

  const found = getRoadShield(road, "");
  const shield = found.kind === "s" ? null : found;
  if (shield) {
    // Wherever the number sits in the name — "A10 Coenplein", "Rijksweg A12" —
    // it comes out. Globally: a way named "N200 · N200" kept its second copy,
    // and the card read "· N200" beside an N200 shield.
    road = road.replace(
      new RegExp(`\\b${shield.label[0]}\\s?${shield.label.slice(1)}\\b`, "gi"), "");
  }
  // Nothing left means the road *was* the number, and the shield says it.
  return { shield, road: tidySeparators(road), rest };
};

/**
 * Rebuild a road name out of its parts, dropping whatever is left empty.
 *
 * Lifting the number out of the name leaves a separator hanging where it used
 * to be, and trimming the ends is not enough for either of the two shapes that
 * actually reach a screen. A road that carries two numbers is tagged
 * ref="N200;S103" in OSM, so the card read ";S103 · Haarlemmerweg" with the
 * semicolon still on the front — 641 m ahead on a real board. And a way whose
 * name is its number twice over came out as "· N200".
 *
 * So instead of trimming, the name is split on its separators and put back
 * together from the pieces that still say something. That also handles the
 * separator being left in the middle — "S103; · Haarlemmerweg" — which no
 * amount of trimming the ends would reach.
 *
 * Kept identical to tidySeparators() in android RoadLine.kt: the two apps have
 * to name a road the same way or a driver meets two different products.
 */
export const tidySeparators = (name) =>
  String(name || "")
    .split(/[;·]/)
    .map(s => s.replace(/\s{2,}/g, " ").replace(/^[\s,\-–]+|[\s,\-–]+$/g, ""))
    .filter(Boolean)
    .join(" · ");
